/**
 * "Draft all" on 026's handoff (spec 027, `allAction`): one job per needed
 * slot with a brief in one request (slots without one are skipped and
 * counted, D-23). The label states the request count, not a price;
 * with fewer units left than the jobs need it is disabled and says so.
 */
import React from 'react';
import type { PictureSlot } from '../../../../Shared/src/@types';
import { DEFAULT_DRAFT_COUNT } from '../../../../Shared/src/images';
import { isSlotNeeded } from '../../../../Shared/src/pictures';
import GenerateButton from './GenerateButton';
import { skippedText } from './imageGenText';
import styles from './ImageGen.module.scss';

export const draftAllLabel = (slots: number, count = DEFAULT_DRAFT_COUNT): string =>
  `Draft all ${slots} (${slots * count} drafts)`;

export const DraftAll: React.FC<{ needed: PictureSlot[] }> = ({ needed: given }) => {
  // Only needed slots, whatever the caller hands in: never a filled one (cast
  // ones included) nor one without uses, which the jobs route rejects.
  const needed = given.filter(isSlotNeeded);
  if (needed.length === 0) return null;
  // A slot without a brief is skipped (D-23).
  const briefed = needed.filter((s) => (s.brief ?? '').trim().length > 0);
  const skipped = needed.length - briefed.length;
  const framings = Object.fromEntries(briefed.filter((s) => s.kind === 'character' && s.framing).map((s) => [s.id, s.framing!]));
  return (
    <>
      {briefed.length > 0 && (
        <GenerateButton
          slotIds={briefed.map((s) => s.id)}
          label={draftAllLabel(briefed.length)}
          request={Object.keys(framings).length > 0 ? { framings } : undefined}
          testId="draft-all"
        />
      )}
      {skipped > 0 && <p className={styles.muted} data-testid="draft-all-skipped">{skippedText(skipped)}</p>}
    </>
  );
};

export default DraftAll;
